import styled from '@emotion/styled';
import _ from 'lodash';

import { useFeaturesWithContext } from './features';
import filterFunc from './filter';
import { useFiltersConfig } from './filtersConfig';
import { ColorWrapper } from '../../ComponentLibrary/filter-tree/oc-color-filter';
import {
  FilterContent,
  FilterHeading,
  FilterSectionHeading,
  FilterSubHeading,
  FilterWrapper,
} from '../../ComponentLibrary/text-elements/filters';
import FilterToggle, { EXCLUSIVE_STRATEGY, FILTERS_WITH_VALUE, MULTISELECT_STRATEGY } from '../../FilterToggle';
import Text from '../../OC/PageBuilder/utilities/Text';
import { If } from '../../Utilities';
import { connectLocation } from '../../location/LocationConnectors';

const ColorFilterTogglesList = ({
  features,
  warranties,
  windResistances,
  impactResistances,
  filtersConfig,
  colors,
  shingles,
  ...props
}) =>
  colors.map(color => (
    <FilterToggle
      key={color}
      filterKey="colors"
      name={filtersConfig.colors.label(color)}
      value={color}
      labelProps={{ color }}
      filterFunc={filterFunc(features, warranties, windResistances, impactResistances)}
      filtersConfig={filtersConfig}
      items={shingles}
      {...props}
    />
  ));

const ShingleFilterTogglesList = ({
  filtersConfig,
  features,
  warranties,
  windResistances,
  impactResistances,
  names,
  shingles,
  t,
  ...props
}) =>
  names.map(name => (
    <FilterToggle
      key={name}
      filterKey="names"
      value={name}
      popular={t('shingles.popular_names').indexOf(name) >= 0}
      filterFunc={filterFunc(features, warranties, windResistances, impactResistances)}
      filtersConfig={filtersConfig}
      items={shingles}
      {...props}
    />
  ));

const VisualStyleSection = ({ style, theseShingles, ...props }) => {
  const names = _.uniq(theseShingles.map(shingle => shingle.proper_name));
  if (
    names.some(({ value }) =>
      filterFunc
        ? !_.isEmpty(
            filterFunc(
              props.features,
              props.warranties,
              props.windResistances,
              props.impactResistances
            )(
              theseShingles,
              FILTERS_WITH_VALUE[EXCLUSIVE_STRATEGY]({ filters: props.filters, filterKey: props.filterKey, value })
            )
          )
        : false
    )
  ) {
    return (
      <div className="filter-group">
        <FilterSubHeading tag="h3" id={`shingle-line-${style}`}>
          {style}
        </FilterSubHeading>

        <ul className="no-border" aria-labelledby={`shingle-line-${style}`} tabIndex="0">
          <ShingleFilterTogglesList names={names} {...props} />
        </ul>
      </div>
    );
  }
  return null;
};

const VisualStyleSectionList = ({ styles, ...props }) =>
  Object.entries(styles).map(([style, theseShingles]) => (
    <VisualStyleSection key={style} style={style} theseShingles={theseShingles} {...props} />
  ));

const PriceFilterTogglesList = ({
  features,
  warranties,
  windResistances,
  impactResistances,
  filtersConfig,
  shingles,
  ...props
}) =>
  filtersConfig.priceRanges.map(price => (
    <FilterToggle
      key={price.uid}
      filterKey="prices"
      name={price.name}
      value={price.uid}
      filterFunc={filterFunc(features, warranties, windResistances, impactResistances)}
      filtersConfig={filtersConfig}
      items={shingles}
      {...props}
    />
  ));

const FeatureFilterToggleList = ({
  filtersConfig,
  features,
  warranties,
  windResistances,
  impactResistances,
  onShowFeature,
  shingles,
  ...props
}) =>
  features.map(feature => (
    <FilterToggle
      key={feature.key}
      name={feature.name}
      value={feature.key}
      alt={feature.alt}
      icon={feature.filterIcon ? feature.filterIcon : feature.icon}
      onInfo={feature.description && (() => onShowFeature(feature))}
      filterFunc={filterFunc(features, warranties, windResistances, impactResistances)}
      filterStrategy={MULTISELECT_STRATEGY}
      filtersConfig={filtersConfig}
      items={shingles}
      {...props}
    />
  ));

const ImpactResistanceFilterToggleList = connectLocation(
  ({
    t,
    locale: { code },
    filtersConfig,
    features,
    warranties,
    windResistances,
    impactResistances,
    filters,
    onShowFeature,
    shingles,
    filterKey,
    forceUpdate,
    ...props
  }) => {
    if (!impactResistances?.length) return null;
    // check if any value in data
    if (
      impactResistances.some(({ value }) =>
        filterFunc
          ? !_.isEmpty(
              filterFunc(
                features,
                warranties,
                windResistances,
                impactResistances
              )(shingles, FILTERS_WITH_VALUE[EXCLUSIVE_STRATEGY]({ filters, filterKey, value }))
            )
          : false
      )
    ) {
      return (
        <fieldset>
          <FilterWrapper>
            <FilterHeading id="filter_impact">
              <Text content={impactResistances[0].singular} />
            </FilterHeading>
            <FilterContent>
              <ul aria-labelledby="filter_impact" tabIndex="0">
                {impactResistances.map(feature => (
                  <FilterToggle
                    filters={filters}
                    key={feature.key}
                    name={feature.name}
                    value={feature.key}
                    icon={feature.icon}
                    alt={feature.alt}
                    onInfo={feature.description && (() => onShowFeature(feature))}
                    filterFunc={filterFunc(features, warranties, windResistances, impactResistances)}
                    filtersConfig={filtersConfig}
                    items={shingles}
                    filterKey={filterKey}
                    {...props}
                  />
                ))}
              </ul>
            </FilterContent>
          </FilterWrapper>
        </fieldset>
      );
    }
    return null;
  }
);

const WarrantyResistanceFilterToggleListFull = connectLocation(
  ({
    t,
    locale: { code },
    filtersConfig,
    features,
    warranties,
    windResistances,
    impactResistances,
    filters,
    onShowFeature,
    shingles,
    filterKey,
    onToggleFilter,
    forceUpdate,
    ...props
  }) => {
    if (!warranties?.length) return null;
    // check if any value in data
    if (
      warranties.some(({ key }) =>
        filterFunc
          ? !_.isEmpty(
              filterFunc(
                features,
                warranties,
                windResistances,
                impactResistances
              )(shingles, FILTERS_WITH_VALUE[EXCLUSIVE_STRATEGY]({ filters, filterKey, value: key }))
            )
          : false
      )
    ) {
      return (
        <fieldset>
          <FilterWrapper>
            <FilterHeading id="filter_warranty">{t('shingles.filters.warranty')}</FilterHeading>
            <FilterContent>
              <ul aria-labelledby="filter_warranty" tabIndex="0">
                <WarrantyResistanceFilterToggleList
                  filtersConfig={filtersConfig}
                  features={features}
                  warranties={warranties}
                  windResistances={windResistances}
                  impactResistances={impactResistances}
                  filters={filters}
                  onToggleFilter={onToggleFilter}
                  shingles={shingles}
                  onShowFeature={onShowFeature}
                  filterKey="warranties"
                />
              </ul>
            </FilterContent>
          </FilterWrapper>
        </fieldset>
      );
    }
    return null;
  }
);

const WindResistanceFilterToggleListFull = connectLocation(
  ({
    t,
    locale: { code },
    filtersConfig,
    features,
    warranties,
    windResistances,
    impactResistances,
    filters,
    onShowFeature,
    shingles,
    filterKey,
    onToggleFilter,
    forceUpdate,
    ...props
  }) => {
    if (!windResistances?.length) return null;
    // check if any value in data
    if (
      windResistances.some(({ key }) =>
        filterFunc
          ? !_.isEmpty(
              filterFunc(
                features,
                warranties,
                windResistances,
                impactResistances
              )(shingles, FILTERS_WITH_VALUE[EXCLUSIVE_STRATEGY]({ filters, filterKey, value: key }))
            )
          : false
      )
    ) {
      return (
        <fieldset>
          <FilterWrapper>
            <FilterHeading id="filter_warranty">{t('shingles.filters.wind_resistances')}</FilterHeading>
            <FilterContent>
              <ul aria-labelledby="filter_wind" tabIndex="0">
                <WindResistanceFilterToggleList
                  filtersConfig={filtersConfig}
                  features={features}
                  warranties={warranties}
                  windResistances={windResistances}
                  impactResistances={impactResistances}
                  filters={filters}
                  onToggleFilter={onToggleFilter}
                  shingles={shingles}
                  onShowFeature={onShowFeature}
                  filterKey="windResistances"
                />
              </ul>
            </FilterContent>
          </FilterWrapper>
        </fieldset>
      );
    }
    return null;
  }
);

const WindResistanceFilterToggleList = ({
  filtersConfig,
  features,
  warranties,
  windResistances,
  impactResistances,
  onShowFeature,
  shingles,
  ...props
}) =>
  windResistances.map(feature => (
    <FilterToggle
      key={feature.key}
      name={feature.name || feature.value}
      value={feature.key}
      icon={feature.icon}
      alt={feature.alt}
      onInfo={feature.description && (() => onShowFeature(feature))}
      filterFunc={filterFunc(features, warranties, windResistances, impactResistances)}
      filtersConfig={filtersConfig}
      items={shingles}
      {...props}
    />
  ));

const WarrantyResistanceFilterToggleList = ({
  filtersConfig,
  features,
  warranties,
  windResistances,
  impactResistances,
  onShowFeature,
  shingles,
  ...props
}) =>
  warranties.map(feature => (
    <FilterToggle
      key={feature.key}
      name={feature.name}
      value={feature.key}
      alt={feature.alt}
      icon={feature.filterIcon ? feature.filterIcon : feature.icon}
      onInfo={feature.description && (() => onShowFeature(feature))}
      filterFunc={filterFunc(features, warranties, windResistances, impactResistances)}
      filterStrategy={MULTISELECT_STRATEGY}
      filtersConfig={filtersConfig}
      items={shingles}
      {...props}
    />
  ));

const FiltersView = ({
  filtersConfig,
  shingles,
  colors,
  visualStyles,
  warranties,
  windResistances,
  impactResistances,
  features,
  filters,
  locale,
  onToggleFilter,
  onShowFeature,
  t,
}) => (
  <div>
    <FilterSectionHeading>{t('shingles.filters.header')}</FilterSectionHeading>

    <fieldset>
      <FilterWrapper>
        <FilterHeading id="filter_color">{t('shingles.filters.colors')}</FilterHeading>
        <FilterContent>
          <ColorWrapper aria-labelledby="filter_color" tabIndex="0">
            <ColorFilterTogglesList
              features={features}
              warranties={warranties}
              windResistances={windResistances}
              impactResistances={impactResistances}
              filtersConfig={filtersConfig}
              colors={colors}
              filters={filters}
              onToggleFilter={onToggleFilter}
              shingles={shingles}
            />
          </ColorWrapper>
        </FilterContent>
      </FilterWrapper>
    </fieldset>

    <fieldset>
      <FilterWrapper>
        <FilterHeading id="filter_shingle">{t('shingles.filters.lines')}</FilterHeading>
        <FilterContent>
          <VisualStyleSectionList
            features={features}
            warranties={warranties}
            windResistances={windResistances}
            impactResistances={impactResistances}
            filtersConfig={filtersConfig}
            styles={visualStyles}
            filters={filters}
            onToggleFilter={onToggleFilter}
            shingles={shingles}
            t={t}
          />
          <ul style={{ paddingBottom: 0 }} />
        </FilterContent>
      </FilterWrapper>
    </fieldset>

    <If condition={!(locale?.code?.toLowerCase() === 'fr-ca' || locale?.code?.toLowerCase() === 'en-ca')}>
      <fieldset>
        <FilterWrapper>
          <FilterHeading id="filter_price">{t('shingles.filters.prices')}</FilterHeading>

          <FilterContent>
            <ul aria-labelledby="filter_price" tabIndex="0">
              <PriceFilterTogglesList
                features={features}
                warranties={warranties}
                windResistances={windResistances}
                impactResistances={impactResistances}
                filtersConfig={filtersConfig}
                filters={filters}
                onToggleFilter={onToggleFilter}
                shingles={shingles}
              />
            </ul>
          </FilterContent>
        </FilterWrapper>
      </fieldset>
    </If>

    <fieldset>
      <FilterWrapper>
        <FilterHeading id="filter_features">{t('shingles.filters.features')}</FilterHeading>
        <FilterContent>
          <ul aria-labelledby="filter_features" tabIndex="0">
            <FeatureFilterToggleList
              filtersConfig={filtersConfig}
              features={features}
              warranties={warranties}
              windResistances={windResistances}
              impactResistances={impactResistances}
              filters={filters}
              onToggleFilter={onToggleFilter}
              shingles={shingles}
              onShowFeature={onShowFeature}
              filterKey="features"
            />
          </ul>
        </FilterContent>
      </FilterWrapper>
    </fieldset>

    <WindResistanceFilterToggleListFull
      filtersConfig={filtersConfig}
      features={features}
      warranties={warranties}
      windResistances={windResistances}
      impactResistances={impactResistances}
      filters={filters}
      onToggleFilter={onToggleFilter}
      shingles={shingles}
      onShowFeature={onShowFeature}
      filterKey="windResistances"
    />
    <ImpactResistanceFilterToggleList
      filtersConfig={filtersConfig}
      onToggleFilter={onToggleFilter}
      features={features}
      warranties={warranties}
      windResistances={windResistances}
      impactResistances={impactResistances}
      filters={filters}
      shingles={shingles}
      onShowFeature={onShowFeature}
      filterKey="impactResistances"
    />
    <WarrantyResistanceFilterToggleListFull
      filtersConfig={filtersConfig}
      features={features}
      warranties={warranties}
      windResistances={windResistances}
      impactResistances={impactResistances}
      filters={filters}
      onToggleFilter={onToggleFilter}
      shingles={shingles}
      onShowFeature={onShowFeature}
      filterKey="warranties"
    />
  </div>
);

const FiltersController = ({ shingles, ...props }) => {
  const features = useFeaturesWithContext();
  const filtersConfig = useFiltersConfig();
  if (!features || !filtersConfig) {
    return null;
  }

  const visualStyles = _.groupBy(shingles, 'visual_style');
  const localFeatures = features.filter(
    f => f.category === 'feature' && f.name && shingles.some(sh => f.isInShingleOrColor(sh))
  );
  const warranties = features.filter(f => f.category === 'warranty');
  const windResistances = features.filter(f => f.category === 'wind_resistance');
  const impactResistances = features.filter(f => f.category === 'impact_resistance');

  return (
    <FiltersView
      filtersConfig={filtersConfig}
      features={localFeatures}
      warranties={warranties}
      windResistances={windResistances}
      impactResistances={impactResistances}
      visualStyles={visualStyles}
      shingles={shingles}
      {...props}
    />
  );
};

export default connectLocation(FiltersController);
